import { useState, useEffect } from "react";
import api from "@/services/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Trash2, ScanLine } from "lucide-react";
import { toast } from "sonner";

const PERFIL_VACIO = {
  nombre: "",
  sectores: "", // texto CSV en el form; se parsea a array al guardar
  tiene_iso: "",
  puede_subrogar: false,
  fuerte_en_tecnica: false,
  solvencia_economica_max: 0,
  notas: "",
  es_srs: false,
};

/**
 * Gestión de perfiles Bisturí (v4.4.35).
 * Admin: lista + crear + borrar perfiles. Un perfil con sectores VACÍOS
 * analiza pliegos de cualquier sector (informe a cliente externo).
 */
export default function PerfilesBisturiDialog({ open, onClose, onChange }) {
  const [perfiles, setPerfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(PERFIL_VACIO);

  const fetchPerfiles = async () => {
    setLoading(true);
    try {
      const res = await api.get("/perfiles-bisturi");
      setPerfiles(res.data?.perfiles || []);
    } catch (e) {
      toast.error("No se pudieron cargar los perfiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPerfiles();
      setForm(PERFIL_VACIO);
    }
  }, [open]);

  const csvToArray = (s) =>
    (s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const crear = async () => {
    if (!form.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    setSaving(true);
    try {
      await api.post("/perfiles-bisturi", {
        id: form.nombre, // el backend lo convierte a slug
        nombre: form.nombre.trim(),
        sectores: csvToArray(form.sectores),
        tiene_iso: csvToArray(form.tiene_iso),
        puede_subrogar: form.puede_subrogar,
        fuerte_en_tecnica: form.fuerte_en_tecnica,
        solvencia_economica_max: Number(form.solvencia_economica_max) || 0,
        notas: form.notas.trim() || null,
        es_srs: form.es_srs,
      });
      toast.success(`Perfil "${form.nombre}" creado`);
      setForm(PERFIL_VACIO);
      await fetchPerfiles();
      onChange?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al crear perfil";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const borrar = async (id, nombre) => {
    if (!window.confirm(`¿Borrar el perfil "${nombre}"?`)) return;
    try {
      await api.delete(`/perfiles-bisturi/${id}`);
      toast.success(`Perfil "${nombre}" borrado`);
      await fetchPerfiles();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al borrar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto theme-bg-secondary theme-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 theme-text">
            <ScanLine className="w-5 h-5 text-purple-500" />
            Perfiles Bisturí
          </DialogTitle>
        </DialogHeader>

        {/* Lista de perfiles existentes */}
        <div className="space-y-2">
          <Label className="theme-text-muted text-xs uppercase tracking-wide">
            Perfiles ({perfiles.length})
          </Label>
          {loading ? (
            <div className="flex items-center gap-2 theme-text-muted text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          ) : (
            <div className="space-y-2">
              {perfiles.map((p) => (
                <div
                  key={p.id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg theme-bg-tertiary theme-border border"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium theme-text truncate">{p.nombre}</span>
                      {p.es_srs ? (
                        <Badge className="bg-teal-600 text-white text-[10px]">SRS</Badge>
                      ) : (
                        <Badge className="bg-amber-600 text-white text-[10px]">Cliente</Badge>
                      )}
                      {(!p.sectores || p.sectores.length === 0) && (
                        <Badge className="bg-slate-500 text-white text-[10px]">
                          Cualquier sector
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs theme-text-muted mt-1 truncate">
                      {p.sectores?.length ? p.sectores.join(", ") : "Sin filtro sectorial"}
                      {p.tiene_iso?.length ? ` · ISO: ${p.tiene_iso.join(", ")}` : ""}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-600 shrink-0"
                    onClick={() => borrar(p.id, p.nombre)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              {perfiles.length === 0 && (
                <p className="text-sm theme-text-muted py-2">Aún no hay perfiles.</p>
              )}
            </div>
          )}
        </div>

        {/* Formulario de creación */}
        <div className="mt-2 pt-4 border-t theme-border space-y-3">
          <Label className="theme-text-muted text-xs uppercase tracking-wide">
            Nuevo perfil
          </Label>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs theme-text-muted">Nombre *</Label>
              <Input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Catering del Norte S.L."
              />
            </div>

            <div className="col-span-2">
              <Label className="text-xs theme-text-muted">
                Sectores (pilares, separados por coma)
              </Label>
              <Input
                value={form.sectores}
                onChange={(e) => setForm({ ...form, sectores: e.target.value })}
                placeholder="Dejar VACÍO = analiza cualquier sector"
              />
              <p className="text-[11px] theme-text-muted mt-1">
                Para un cliente externo de otro sector, déjalo vacío: no se aplicará el
                filtro FUERA_SECTOR.
              </p>
            </div>

            <div className="col-span-2">
              <Label className="text-xs theme-text-muted">
                ISO que posee (separadas por coma)
              </Label>
              <Input
                value={form.tiene_iso}
                onChange={(e) => setForm({ ...form, tiene_iso: e.target.value })}
                placeholder="ISO 9001, ISO 22000…"
              />
            </div>

            <div>
              <Label className="text-xs theme-text-muted">Solvencia máx. (€)</Label>
              <Input
                type="number"
                value={form.solvencia_economica_max}
                onChange={(e) =>
                  setForm({ ...form, solvencia_economica_max: e.target.value })
                }
              />
            </div>

            <div className="flex flex-col justify-center gap-2 pt-4">
              <label className="flex items-center gap-2 text-sm theme-text cursor-pointer">
                <Checkbox
                  checked={form.puede_subrogar}
                  onCheckedChange={(v) => setForm({ ...form, puede_subrogar: !!v })}
                />
                Puede subrogar plantilla
              </label>
              <label className="flex items-center gap-2 text-sm theme-text cursor-pointer">
                <Checkbox
                  checked={form.fuerte_en_tecnica}
                  onCheckedChange={(v) => setForm({ ...form, fuerte_en_tecnica: !!v })}
                />
                Fuerte en memoria técnica
              </label>
              <label className="flex items-center gap-2 text-sm theme-text cursor-pointer">
                <Checkbox
                  checked={form.es_srs}
                  onCheckedChange={(v) => setForm({ ...form, es_srs: !!v })}
                />
                Partner interno SRS
              </label>
            </div>

            <div className="col-span-2">
              <Label className="text-xs theme-text-muted">Notas</Label>
              <Textarea
                rows={2}
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                placeholder="Contexto del cliente/partner…"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={crear}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Crear perfil
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
